import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject, map, switchMap, tap } from 'rxjs';
import { IGenericJSON } from '../helpers/interface';
import { AppNotification } from '../models/notification';
import { WebsocketService } from './websocket.service';

interface ClearedPayload {
  entityType: string;
  entityId: number;
}

/** Matches the server's default page size. */
const PAGE_SIZE = 20;

/**
 * Holds the current user's notifications and keeps the nav badge in sync.
 *
 * The list and count are always established by a fetch.
 * Live delivery over the socket is layered on top of this.
 */
@Injectable({ providedIn: 'root' })
export class NotificationService {
  private http = inject(HttpClient);
  private websocket = inject(WebsocketService);
  private base = '/api/notifications';

  /** Guards against a second listener if start() is called more than once. */
  private listening = false;

  private notifications = new BehaviorSubject<AppNotification[]>([]);
  /** Active (undismissed) notifications, newest first. */
  notifications$ = this.notifications.asObservable();

  private unreadCount = new BehaviorSubject<number>(0);
  /** Drives the nav badge. Server-computed, since the list is paged. */
  unreadCount$ = this.unreadCount.asObservable();

  private arrived = new Subject<AppNotification>();
  /**
   * Emits only for notifications pushed over the socket — never for ones pulled
   * in by a fetch. Anything reacting to arrival (the toast) needs "this just
   * happened", not "this exists".
   */
  arrived$ = this.arrived.asObservable();

  private hasMore = new BehaviorSubject<boolean>(false);
  /** Whether the server has notifications beyond the ones loaded. */
  hasMore$ = this.hasMore.asObservable();

  /**
   * Fetches current state and begins listening. Called once, on login.
   *
   * Listening starts on each connect rather than at startup because
   * WebsocketService replaces this.socket on reconnect (orphaning handlers bound
   * to the old one) and its on() never retries a socket that didn't exist yet.
   * Re-binding per connect also covers the re-sync, since socket.io drops emits
   * to disconnected clients. switchMap discards the stale listener.
   */
  start(): void {
    this.refresh();

    if (this.listening) return;
    this.listening = true;

    this.websocket
      .onReconnect()
      .pipe(
        tap(() => this.refresh()),
        switchMap(() => this.websocket.on<IGenericJSON>('notification:new')),
      )
      .subscribe((raw) => this.addFromSocket(raw));

    this.websocket
      .onReconnect()
      .pipe(
        switchMap(() => this.websocket.on<ClearedPayload>('notification:cleared')),
      )
      .subscribe((payload) => this.clearForEntity(payload.entityType, payload.entityId));
  }

  /** Pulls list and badge from the server, discarding local state. */
  refresh(): void {
    this.load().subscribe({ error: () => undefined });
    this.loadUnreadCount().subscribe({ error: () => undefined });
  }

  /**
   * Replaces local state from the server. Safe to call repeatedly — on login,
   * and again after any socket reconnect, where events may have been missed.
   */
  load(): Observable<AppNotification[]> {
    return this.http.get<IGenericJSON[]>(this.base, { params: { limit: PAGE_SIZE } }).pipe(
      map((rows) => AppNotification.digestResponse(rows)),
      tap((list) => {
        this.notifications.next(list);
        this.hasMore.next(list.length === PAGE_SIZE);
      }),
    );
  }

  /**
   * Appends the next page. The offset is the current list length rather than a
   * page counter, so anything dismissed in between doesn't cause a skip.
   */
  loadMore(): Observable<AppNotification[]> {
    return this.http
      .get<IGenericJSON[]>(this.base, {
        params: { limit: PAGE_SIZE, offset: this.notifications.value.length },
      })
      .pipe(
        map((rows) => AppNotification.digestResponse(rows)),
        tap((page) => {
          // Guard against a duplicate if something arrived over the socket
          // between the two requests and shifted the window.
          const known = new Set(this.notifications.value.map((n) => n.id));
          const fresh = page.filter((n) => !known.has(n.id));

          this.notifications.next([...this.notifications.value, ...fresh]);
          this.hasMore.next(page.length === PAGE_SIZE);
        }),
      );
  }

  /**
   * The badge count, which has to come from the server: the list is paged, so
   * counting what was fetched under-reports once a user has more than a page
   * of unread notifications.
   */
  loadUnreadCount(): Observable<number> {
    return this.http.get<{ count: number }>(`${this.base}/unread-count`).pipe(
      map((res) => res.count ?? 0),
      tap((count) => this.unreadCount.next(count)),
    );
  }

  markRead(id: number): Observable<AppNotification> {
    return this.http.patch<IGenericJSON>(`${this.base}/${id}/read`, {}).pipe(
      map((row) => AppNotification.digest(row)),
      tap((updated) => {
        if (!this.find(updated.id)?.isRead) this.adjustCount(-1);
        this.notifications.next(
          this.notifications.value.map((n) => (n.id === updated.id ? updated : n)),
        );
      }),
    );
  }

  /** Opening the alerts dropdown clears the badge but keeps the list. */
  markAllRead(): Observable<number> {
    return this.http.patch<{ updated: number }>(`${this.base}/read-all`, {}).pipe(
      map((res) => res.updated ?? 0),
      tap(() => {
        const now = new Date();
        // New instances rather than mutating in place: editing the objects
        // already inside the BehaviorSubject changes them before the emission,
        // and leaves identity-based change detection with nothing to notice.
        this.notifications.next(
          this.notifications.value.map((n) =>
            n.isRead ? n : AppNotification.digest({ ...n, readAt: now }),
          ),
        );
        this.unreadCount.next(0);
      }),
    );
  }

  /** Removes a notification from the list. The row is kept server-side. */
  dismiss(id: number): Observable<AppNotification> {
    return this.http.patch<IGenericJSON>(`${this.base}/${id}/dismiss`, {}).pipe(
      map((row) => AppNotification.digest(row)),
      tap((dismissed) => {
        if (!this.find(dismissed.id)?.isRead) this.adjustCount(-1);
        this.notifications.next(this.notifications.value.filter((n) => n.id !== dismissed.id));
      }),
    );
  }

  /**
   * Clears the whole list in one call, so a long backlog doesn't have to be
   * dismissed one at a time. The rows are kept server-side, same as dismiss().
   */
  dismissAll(): Observable<number> {
    return this.http.patch<{ updated: number }>(`${this.base}/dismiss-all`, {}).pipe(
      map((res) => res.updated ?? 0),
      tap(() => {
        this.notifications.next([]);
        this.unreadCount.next(0);
      }),
    );
  }

  /**
   * Marks unread notifications for an entity read — the server emits this when
   * the user opens that entity directly (e.g. a conversation from the sidebar),
   * so the bell stops claiming there's something new to look at.
   */
  private clearForEntity(entityType: string, entityId: number): void {
    const now = new Date();
    const matching = this.notifications.value.filter(
      (n) => !n.isRead && n.entityType === entityType && n.entityId === entityId,
    );
    if (!matching.length) return;

    this.notifications.next(
      this.notifications.value.map((n) =>
        matching.includes(n) ? AppNotification.digest({ ...n, readAt: now }) : n,
      ),
    );
    this.adjustCount(-matching.length);
  }

  /**
   * Inserts or refreshes a notification pushed over the socket.
   *
   * `isUpdate` (wire-only, set by notify()'s groupKey collapsing) tells
   * these two already-known-id cases apart:
   *  - isUpdate true: a later event in the same burst (e.g. more messages in
   *    a conversation) refreshed the existing row's content server-side —
   *    update it in place so the bell shows the latest snippet, but don't
   *    toast or recount something the user was already told about.
   *  - isUpdate false: a genuine duplicate delivery of something already
   *    present (e.g. a reconnect re-fetch racing the original live event) —
   *    ignore it outright, as this always has.
   */
  addFromSocket(raw: IGenericJSON): void {
    const incoming = AppNotification.digest(raw);
    const isUpdate = raw['isUpdate'] === true;
    const index = this.notifications.value.findIndex((n) => n.id === incoming.id);

    if (index !== -1) {
      if (!isUpdate) return;

      const updated = [...this.notifications.value];
      updated[index] = incoming;
      this.notifications.next(updated);
      return;
    }

    this.notifications.next([incoming, ...this.notifications.value]);
    if (!incoming.isRead) this.adjustCount(1);

    this.arrived.next(incoming);
  }

  private find(id: number): AppNotification | undefined {
    return this.notifications.value.find((n) => n.id === id);
  }

  /**
   * Nudges the badge rather than recounting the list, which only holds the
   * current page and would drop the count to whatever happens to be loaded.
   */
  private adjustCount(delta: number): void {
    this.unreadCount.next(Math.max(0, this.unreadCount.value + delta));
  }
}
